/**
 * Client-side image thumbnail generation
 * Used as fallback when Cloudflare Images is not available
 */

import type { ImageFolderPhoto, SubfolderType } from "@shared/api";
import { apiUrl, toAbsoluteAssetUrl } from "./api-client";

/**
 * Generate thumbnail from image bytes using Canvas API
 * @param source - Image file or blob
 * @param maxSize - Maximum width/height for thumbnail (default: 300)
 * @returns Promise resolving to Blob containing thumbnail image
 */
export async function generateThumbnail(
  source: Blob | File,
  maxSize: number = 300
): Promise<Blob | null> {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(source);
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      console.error("[Thumbnail] Canvas context not available");
      URL.revokeObjectURL(objectUrl);
      resolve(null);
      return;
    }

    img.onload = () => {
      try {
        // Calculate dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw image to canvas
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(objectUrl);

        // Convert to blob (prefer WebP, fallback to JPEG)
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              console.error("[Thumbnail] Failed to create blob");
              resolve(null);
            }
          },
          "image/webp",
          0.85 // Quality: 0.85 (85%)
        );
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        console.error("[Thumbnail] Error generating thumbnail:", error);
        resolve(null);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      console.error("[Thumbnail] Failed to load image");
      resolve(null);
    };

    img.src = objectUrl;
  });
}

/**
 * Upload thumbnail to server
 * @param thumbnailBlob - Thumbnail blob to upload
 * @param folderId - Folder ID
 * @param subfolderType - Subfolder type
 * @param filename - Original filename
 * @param photoId - Photo ID to update in database (optional)
 * @returns Promise resolving to thumbnail URL or null
 */
export async function uploadThumbnail(
  thumbnailBlob: Blob,
  folderId: string,
  subfolderType: "bemeres" | "munkaink" | "marketing",
  filename: string,
  photoId?: string
): Promise<string | null> {
  try {
    const formData = new FormData();
    formData.append("thumbnail", thumbnailBlob, `${filename}.thumb`);
    formData.append("subfolderType", subfolderType);
    if (photoId) {
      formData.append("photoId", photoId);
    }

    const response = await fetch(apiUrl(`/api/images/folders/${folderId}/photos/thumbnail`), {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      console.error("[Thumbnail] Upload failed:", error);
      return null;
    }

    const data = await response.json();
    return data.thumbnailUrl || null;
  } catch (error) {
    console.error("[Thumbnail] Error uploading thumbnail:", error);
    return null;
  }
}

/**
 * Generate and upload thumbnail for an image
 * @param file - Image file
 * @param folderId - Folder ID
 * @param subfolderType - Subfolder type
 * @param photoId - Photo ID to update in database (optional)
 * @returns Promise resolving to thumbnail URL or null
 */
export async function generateAndUploadThumbnail(
  file: File,
  folderId: string,
  subfolderType: "bemeres" | "munkaink" | "marketing",
  photoId?: string
): Promise<string | null> {
  console.log(`[Thumbnail] generateAndUploadThumbnail called for ${file.name}, photoId: ${photoId || 'none'}`);
  
  // Only process image files
  if (!file.type.startsWith("image/")) {
    console.log(`[Thumbnail] Skipping non-image file: ${file.name} (type: ${file.type})`);
    return null;
  }

  try {
    console.log(`[Thumbnail] Generating thumbnail for ${file.name}...`);
    const thumbnailBlob = await generateThumbnail(file, 300);
    if (!thumbnailBlob) {
      console.error(`[Thumbnail] Failed to generate thumbnail blob for ${file.name}`);
      return null;
    }
    console.log(`[Thumbnail] Thumbnail blob generated for ${file.name}, size: ${thumbnailBlob.size} bytes`);

    console.log(`[Thumbnail] Uploading thumbnail for ${file.name}...`);
    const thumbnailUrl = await uploadThumbnail(thumbnailBlob, folderId, subfolderType, file.name, photoId);
    if (thumbnailUrl) {
      console.log(`[Thumbnail] Successfully uploaded thumbnail for ${file.name}: ${thumbnailUrl}`);
    } else {
      console.error(`[Thumbnail] Failed to upload thumbnail for ${file.name}`);
    }
    return thumbnailUrl;
  } catch (error) {
    console.error("[Thumbnail] Error in generateAndUploadThumbnail:", error);
    return null;
  }
}

function normalizeSubfolderType(st: string | undefined): SubfolderType {
  return st === "bemeres" || st === "munkaink" || st === "marketing" ? st : "munkaink";
}

/**
 * Download existing gallery/full image and upload a resized thumbnail (for rows where thumbnail_url is null).
 */
export async function generateAndUploadThumbnailFromExistingPhoto(
  photo: Pick<
    ImageFolderPhoto,
    "id" | "folder_id" | "file_name" | "file_url" | "subfolder_type" | "thumbnail_url"
  >
): Promise<string | null> {
  if (photo.thumbnail_url) return photo.thumbnail_url;
  if (!photo.file_url) return null;

  const subfolderType = normalizeSubfolderType(photo.subfolder_type);

  try {
    const imageUrl = toAbsoluteAssetUrl(photo.file_url);
    const res = await fetch(imageUrl, { credentials: "include" });
    if (!res.ok) return null;
    let blob = await res.blob();
    const mime = blob.type || "";
    if (!mime.startsWith("image/")) {
      const lower = (photo.file_name || "").toLowerCase();
      const guess =
        lower.endsWith(".png") ? "image/png" :
        lower.endsWith(".webp") ? "image/webp" :
        lower.endsWith(".gif") ? "image/gif" :
        lower.endsWith(".bmp") ? "image/bmp" :
        "image/jpeg";
      blob = new Blob([blob], { type: guess });
    }

    const thumbnailBlob = await generateThumbnail(blob, 300);
    if (!thumbnailBlob) return null;

    return await uploadThumbnail(
      thumbnailBlob,
      photo.folder_id,
      subfolderType,
      photo.file_name,
      photo.id
    );
  } catch {
    return null;
  }
}
